// Pins: a field the deployment sets (an IaC parameter) is read-only in the form, and the form says
// which parameter pins it. A pin on an object pins everything under it.
export type Pins = Readonly<Record<string, string>>;

/** The parameter pinning `path` (or an ancestor of it), or null. */
export function pinFor(pins: Pins | undefined, path: string): string | null {
  if (!pins) return null;
  const direct = pins[path];
  if (direct !== undefined) return direct;
  for (const pinned of Object.keys(pins)) {
    if (path.startsWith(`${pinned}.`)) return pins[pinned] ?? null;
  }
  return null;
}

export type Principal = "customer" | "staff";

export type FieldMode =
  | { kind: "editable" }
  | { kind: "readonly"; reason: "pinned" | "customer-readonly" | "staff-only" | "server-generated" | "secret"; note: string };

interface Rules {
  customer_editable: boolean;
  customer_readonly: boolean;
  staff_only: boolean;
  server_generated: boolean;
  secret_ref: boolean;
}

/**
 * Whether the principal can edit the field. Pinned, server-generated and secret-reference fields
 * are read-only to everyone; customer-readonly and staff-only fields are editable by staff only
 * (who give a reason with the write); the rest follows x-nexus-customer-editable.
 */
export function fieldMode(rules: Rules, principal: Principal, pin: string | null): FieldMode {
  if (pin !== null) return { kind: "readonly", reason: "pinned", note: `Pinned by deployment (${pin})` };
  if (rules.server_generated) return { kind: "readonly", reason: "server-generated", note: "Set by Nexus" };
  if (rules.secret_ref) return { kind: "readonly", reason: "secret", note: "Only the secret's name is kept here; its value is set elsewhere" };
  if (principal === "staff") return { kind: "editable" };
  if (rules.customer_readonly) return { kind: "readonly", reason: "customer-readonly", note: "Read-only; AllCode changes this for you" };
  if (rules.staff_only || !rules.customer_editable) return { kind: "readonly", reason: "staff-only", note: "Changed by AllCode staff" };
  return { kind: "editable" };
}
